import React, {
  useState,
  useContext,
  useImperativeHandle,
  forwardRef,
} from "react";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { PDFDocument } from "pdf-lib";
import "./PrescriptionPreview.scss";

import {
  formatDateIST,
  formatAppointmentTypeForPdf,
  getHumanReadableDiffInPastDateWithAgo,
  getHumanReadableDurationInDate,
} from "../../pages/doctor-console/Utils/UtilsFunctions";
import {
  formatHistoryOnsetForPdf,
  formatHistoryDurationForPdf,
} from "../../pages/doctor-console/Utils/pdfHistoryFormatters";
import { formatOnsetDurationForPdf } from "../../pages/doctor-console/HistoriesData/durationUtils";

import { sections } from "../../pages/doctor-console/initialData/initialData";
import { BrandingContext } from "../../BrandingProvider";
import { usePermissions } from "../../pages/login/PermissionContext";
import NavLogoFooter from "../../assets/images/logo/NavLogo_new.png";
import { isTodayOrFuture } from "../../pages/utils/utils";
import { displayPatientName } from "../../utils/displayPatientName";
import { displayDoctorName } from "../../utils/displayDoctorName";
const SECTION_SPACING = 10; // uniform spacing between sections
const TABLE_MARGIN_TOP = 0; // for autoTables, since we control Y manually

const PrintPrescription = forwardRef(
  (
    {
      selectedSectionsData,
      historyDetails,
      sectionsContent,
      doctorData,
      hasVitalValue,
      vitals,
      hasDentalData,
      dentalData,
      prescribedMedicines,
      patientData,
      bloodGroup,
      otherExaminations,
      selectedContext,
      selectedContextDisplay,
      followUpError,
      referralError,
      departments,
      showFooter: showFooterProp = true,
      showTopHeader: showTopHeaderProp = true,
      dateForAppointment,
      startTime,
    },
    ref
  ) => {
    const [newSectionsWithData, setNewSectionsWithData] = useState(sections);
    const { userInfo } = usePermissions();

    const brandingData = useContext(BrandingContext);

    // Get template config from brandingData (context API)
    const prescriptionConfigItem = brandingData?.find(
      (item) =>
        item.name === "prescription_config" || item.name === "template_config"
    );
    const templateConfigFromContext = prescriptionConfigItem?.content
      ? typeof prescriptionConfigItem.content === "string"
        ? JSON.parse(prescriptionConfigItem.content)
        : prescriptionConfigItem.content
      : null;

    // Get template from brandingData (context API) or fallback to userInfo
    const prescriptionTemplateItem = brandingData?.find(
      (item) =>
        item.name === "prescription_template" || item.name === "template"
    );
    const templateBase64 =
      prescriptionTemplateItem?.content || userInfo?.template || null;
    const hasTemplateBackground = Boolean(templateBase64);

    // Merge template config: context API > userInfo > props > defaults
    const templateConfig = {
      showTopHeader:
        templateConfigFromContext?.showTopHeader ??
        userInfo?.template_config?.showTopHeader ??
        showTopHeaderProp ??
        true,
      showFooter:
        templateConfigFromContext?.showFooter ??
        userInfo?.template_config?.showFooter ??
        showFooterProp ??
        true,
      extraBottomMargin:
        templateConfigFromContext?.extraBottomMargin ??
        userInfo?.template_config?.extraBottomMargin ??
        0,
      extraRightMargin:
        templateConfigFromContext?.extraRightMargin ??
        userInfo?.template_config?.extraRightMargin ??
        0,
      extraLeftMargin:
        templateConfigFromContext?.extraLeftMargin ??
        userInfo?.template_config?.extraLeftMargin ??
        0,
      extraTopMargin:
        templateConfigFromContext?.extraTopMargin ??
        userInfo?.template_config?.extraTopMargin ??
        0,
      useBackgroundTemplate:
        templateConfigFromContext?.useBackgroundTemplate ??
        userInfo?.template_config?.useBackgroundTemplate ??
        false,
      printOfsetTop:
        templateConfigFromContext?.printOfsetTop ??
        userInfo?.template_config?.printOfsetTop ??
        0,
      printOfsetLeft:
        templateConfigFromContext?.printOfsetLeft ??
        userInfo?.template_config?.printOfsetLeft ??
        0,
      showDoctorSignature:
        templateConfigFromContext?.showDoctorSignature ??
        userInfo?.doctor_requires_prescription_signature ??
        userInfo?.template_config?.showDoctorSignature ??
        true,
    };

    console.log("DoctorData from preview page", doctorData);
    console.log(
      "Selected Section Data from preview page",
      selectedSectionsData
    );
    console.log("History Details", historyDetails);
    const brandingItem = brandingData?.find(
      (item) => item.name === "branding_statements"
    );
    const collateralBrandStatement =
      brandingItem?.content?.collateralBrandStatement || "";
    const collateralImage =
      brandingData?.find((item) => item.name === "collateral_image")?.content ||
      "";
    // Optional full-page/background overlay image (base64) for pre-printed prescription
    // Try from branding first; fallback to sessionStorage key 'prescriptionOverlayImage'
    const overlayImageBase64 =
      brandingData?.find((item) => item.name === "prescription_overlay_image")
        ?.content ||
      sessionStorage.getItem("prescriptionOverlayImage") ||
      "";
    const pageWidth = 595.28;
    const pageHeight = 841.89;
    const baseMargin = 40;

    // Extract configuration values
    const {
      showTopHeader: configShowTopHeader = true,
      showFooter: configShowFooter = true,
      extraBottomMargin = 0,
      extraRightMargin = 0,
      extraLeftMargin = 0,
      extraTopMargin = 0,
      useBackgroundTemplate = false,
      printOfsetTop = 0,
      printOfsetLeft = 0,
    } = templateConfig;

    // Calculate effective margins (base + extra from config)
    const EFFECTIVE_LEFT_MARGIN = baseMargin + extraLeftMargin;
    const EFFECTIVE_TOP_MARGIN = baseMargin + extraTopMargin;

    // Extra printable area offsets for pre-printed stationery (separate from extra margins)
    const PRINT_OFFSET_LEFT = printOfsetLeft;
    const PRINT_OFFSET_TOP = printOfsetTop; // increase to move content down

    // Optional overlay (watermark) on top of content
    const SHOW_RX_OVERLAY = false; // set to true to enable text/image watermark
    const RX_OVERLAY_OPACITY = 0.08; // keep low so text remains readable
    // Footer visibility flag - use config from context API, fallback to prop
    const SHOW_FOOTER =
      typeof configShowFooter === "boolean"
        ? configShowFooter
        : typeof showFooterProp === "boolean"
          ? showFooterProp
          : true;

    const CONTENT_BOTTOM_MARGIN = 80;
    // Additional configurable bottom margin (in points) to push content up
    const EXTRA_CONTENT_BOTTOM_MARGIN = Number(extraBottomMargin ?? 0) || 0;
    const EFFECTIVE_BOTTOM_MARGIN =
      CONTENT_BOTTOM_MARGIN + EXTRA_CONTENT_BOTTOM_MARGIN;

    // Additional configurable right margin for content and tables
    const EXTRA_RIGHT_MARGIN = Number(extraRightMargin ?? 0) || 0;
    const EFFECTIVE_RIGHT_MARGIN = baseMargin + EXTRA_RIGHT_MARGIN;
    const RIGHT_EDGE_OFFSET = baseMargin + EXTRA_RIGHT_MARGIN;

    // Toggle top header visibility - use config from context API, fallback to prop
    const SHOW_TOP_HEADER =
      typeof configShowTopHeader === "boolean"
        ? configShowTopHeader
        : typeof showTopHeaderProp === "boolean"
          ? showTopHeaderProp
          : true;
    // Static content top margin for sections below header

    // Compute the Y position where main content should start, based on actual header height.
    // Mirrors renderHeaderFooter calculations (without drawing) so content never overlaps the header.
    const computeContentStartY = (doc) => {
      const baseY = EFFECTIVE_TOP_MARGIN + PRINT_OFFSET_TOP;
      const doctorY = baseY + 40;
      const midX = pageWidth / 2 + 10 + PRINT_OFFSET_LEFT;
      const clinicAddressY = doctorY + 45;
      const rightColumnLineHeight = 12;
      const lineHeight = 12;
      const lineSpacing = 5;

      // Left column height (doctor details are only shown when top header is enabled)
      let leftY = doctorY;
      let wrappedClinicAddress = [];
      if (SHOW_TOP_HEADER) {
        leftY = doctorY + 16; // bold 14pt name
        const qualificationsText = `${doctorData?.qualifications || ""}`;
        if (qualificationsText.trim()) {
          const wrappedQual = doc.splitTextToSize(
            qualificationsText,
            pageWidth / 2 - 40
          );
          leftY += wrappedQual.length * 12;
        }
        leftY += 12; // Reg No
        if (doctorData?.phone_no) leftY += 12; // Contact
        if (doctorData?.email) leftY += 12; // Email

        // Right column height (clinic address)
        const clinicAddress = [
          sessionStorage.getItem("facilityAddressLine1"),
          sessionStorage.getItem("facilityAddressLine2"),
          sessionStorage.getItem("facilityAddressLine3"),
        ]
          .filter(Boolean)
          .join(", ");
        const rightColumnWidth = Math.max(80, pageWidth - midX - 40);
        wrappedClinicAddress = doc.splitTextToSize(
          `Clinic Address: ${clinicAddress}`,
          rightColumnWidth
        );
      }
      const rightColumnBottom =
        clinicAddressY + wrappedClinicAddress.length * rightColumnLineHeight + 5;

      const patientY = Math.max(leftY + 5, rightColumnBottom);

      // Patient section
      const availableWidth =
        pageWidth - (EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT) - EFFECTIVE_RIGHT_MARGIN;
      const patientLine = [
        patientData?.patient_id ? `ID: ${patientData.patient_id}` : null,
        `Patient: ${displayPatientName(patientData?.name)}`,
      ].filter(Boolean).join(" | ");
      const wrappedPatientLine = doc.splitTextToSize(patientLine, availableWidth);
      let patientSectionY = patientY + wrappedPatientLine.length * lineHeight + lineSpacing;

      const appointmentDateTime = (() => {
        if (!dateForAppointment) return null;
        const formattedDateTime = formatDateIST(dateForAppointment.toISOString());
        const datePart = formattedDateTime?.split(" ")?.[0];
        const timePart = isTodayOrFuture(dateForAppointment)
          ? formatDateIST(new Date().toISOString()).split(" ").slice(1).join(" ")
          : startTime;
        if (!datePart && !timePart) return null;
        return `DT: ${[datePart, timePart].filter(Boolean).join(" ")}`;
      })();
      const patientContactLine = [
        `Appt: ${formatAppointmentTypeForPdf(
          patientData?.appointment_type,
          patientData?.appointment_mode
        )}`,
        patientData?.phone_no ? `Ph: ${patientData.phone_no}` : null,
        patientData?.age ? `Age: ${patientData.age}y` : null,
        patientData?.gender ? `G: ${patientData.gender}` : null,
        appointmentDateTime,
      ].filter(Boolean).join(" | ");
      if (patientContactLine) {
        const wrappedContactLine = doc.splitTextToSize(patientContactLine, availableWidth);
        patientSectionY += wrappedContactLine.length * lineHeight + lineSpacing;
      }

      const abhaParts = [];
      if (patientData?.abha_id) abhaParts.push(`ABHA Address: ${patientData.abha_id}`);
      if (patientData?.abha_no) abhaParts.push(`ABHA Number: ${patientData.abha_no}`);
      if (abhaParts.length > 0) {
        const wrappedAbhaLine = doc.splitTextToSize(
          abhaParts.join(" | "),
          availableWidth
        );
        patientSectionY += wrappedAbhaLine.length * lineHeight + lineSpacing;
      }

      // separator (5) + 2 units space before first content section
      return patientSectionY + 5 + 2;
    };

    // Ensure there is enough space to start a new section; otherwise move to next page
    const ensureSpaceForSection = (doc, startY, minNeeded = 145) => {
      const available = pageHeight - startY - EFFECTIVE_BOTTOM_MARGIN;
      if (available < minNeeded) {
        doc.addPage();
        // draw header on the new page for consistency
        renderHeaderFooter({}, doc);
        return computeContentStartY(doc);
      }
      return startY;
    };

    // Main PDF Export Function
    // Main PDF Export Function
    const handleDownload = async (withHeaders, type) => {
      const doc = new jsPDF({
        orientation: "p",
        unit: "pt",
        format: "a4",
        compress: true,
      });

      // Override useBackgroundTemplate based on type and withHeaders for OpConsultation
      let shouldUseTemplate = useBackgroundTemplate && hasTemplateBackground;
      if (type === "OpConsultation") {
        // For OP Consultation: use template when withHeaders is true, don't use when false
        shouldUseTemplate = withHeaders === true && hasTemplateBackground;
      }
      // For Prescription type, use the existing config (shouldUseTemplate remains as useBackgroundTemplate)

      const brandingItem = brandingData?.find(
        (item) => item.name === "branding_statements"
      );
      const themeColour = brandingItem?.content?.themeColour || "#21cdc0";

      // Start after header (dynamic — grows with qualifications / patient data)
      let currentY = computeContentStartY(doc);
      currentY = renderChiefComplaintsToPdf(
        doc,
        currentY,
        themeColour,
        selectedSectionsData["Chief Complaints"] || []
      );

      currentY = renderInitialObservationToPdf(
        doc,
        currentY,
        themeColour,
        selectedSectionsData["InitialObservation"] || []
      );

      const renderHistorySectionGroup = (
        doc,
        startY,
        themeColour,
        title,
        entries
      ) => {
        let currentY = startY;
        const hasAnyData = Object.values(entries).some(
          (section) =>
            (section?.prev?.length ?? 0) > 0 || (section?.new?.length ?? 0) > 0
        );

        if (!hasAnyData) {
          return currentY; // Skip rendering completely
        }

        const renderLabel = (text, fontSize = 12) => {
          doc.setFontSize(fontSize);
          doc.setTextColor("#000");
          doc.text(text, EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT, currentY);
          currentY += 15;
        };
        //Added small gap before starting History section
        currentY += 10;
        renderLabel("History", 14);

        const recentSections = [];
        const oldSections = [];

        for (const [sectionName, sectionValue] of Object.entries(entries)) {
          const prev = sectionValue?.prev ?? [];
          const current = sectionValue?.new ?? [];
          if (current.length > 0) {
            recentSections.push({ sectionName, data: current });
          }
          if (prev.length > 0) {
            oldSections.push({ sectionName, data: prev });
          }
        }

        if (recentSections.length > 0) {
          renderLabel("Recent Data", 12);
          for (const section of recentSections) {
            currentY += 8;
            renderLabel(`• ${section.sectionName}`, 10);
            currentY = ensureSpaceForSection(doc, currentY);
            autoTable(doc, {
              startY: currentY,
              head: [getHeaders(section.sectionName)],
              body: section.data.map((item) =>
                formatRowData(section.sectionName, item)
              ),
              theme: "striped",
              styles: { fontSize: 8 },
              headStyles: {
                fillColor: hexToRgbArray("#EEEEEE"),
                textColor: hexToRgbArray("#000000"),
              },
              margin: {
                top: 250 + PRINT_OFFSET_TOP,
                left: EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
                right: EFFECTIVE_RIGHT_MARGIN,
                bottom: EFFECTIVE_BOTTOM_MARGIN,
              },
              didDrawPage: (data) => renderHeaderFooter(data, doc),
              pageBreak: "auto",
            });
            currentY = doc.lastAutoTable.finalY + 7;
          }
        }

        if (oldSections.length > 0) {
          renderLabel("Old Data", 12);
          for (const section of oldSections) {
            currentY += 8;
            renderLabel(`• ${section.sectionName}`, 10);
            currentY = ensureSpaceForSection(doc, currentY);
            autoTable(doc, {
              startY: currentY,
              head: [getHeaders(section.sectionName)],
              body: section.data.map((item) =>
                formatRowData(section.sectionName, item)
              ),
              theme: "striped",
              styles: { fontSize: 8 },
              headStyles: {
                fillColor: hexToRgbArray("#EEEEEE"),
                textColor: hexToRgbArray("#000000"),
              },
              margin: {
                top: 250 + PRINT_OFFSET_TOP,
                left: EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
                right: EFFECTIVE_RIGHT_MARGIN,
                bottom: EFFECTIVE_BOTTOM_MARGIN,
              },
              didDrawPage: (data) => renderHeaderFooter(data, doc),
              pageBreak: "auto",
            });
            currentY = doc.lastAutoTable.finalY + 7;
          }
        }

        return currentY;
      };

      currentY = renderHistorySectionGroup(
        doc,
        currentY,
        themeColour,
        "History",
        historyDetails
      );

      // 5. Render Examinations
      currentY = renderExaminationsToPdf(
        doc,
        currentY,
        themeColour,
        vitals,
        bloodGroup,
        otherExaminations,
        dentalData
      );

      //6. Render Investigations
      currentY = renderInvestigationToPdf(
        doc,
        currentY,
        themeColour,
        selectedSectionsData["Tests/Investigations"] || []
      );
      // 7. Render Other Sections
      currentY = renderOtherSectionsToPdf(
        doc,
        currentY,
        themeColour,
        selectedSectionsData["Diagnosis"] || []
      );

      currentY = renderAdultVaccinationToPdf(
        doc,
        currentY,
        themeColour,
        selectedSectionsData["Adult Vaccination"] ||
        selectedSectionsData["Adult Vaccinations"] ||
        []
      );

      if (selectedSectionsData["Medications"]?.length > 0) {
        currentY = ensureSpaceForSection(doc, currentY);
        currentY += SECTION_SPACING + 2; // space above Medications section
        doc.setFontSize(14);
        doc.setTextColor("#000");
        doc.text(
          "Medications",
          EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
          currentY
        );
        currentY += 15;

        autoTable(doc, {
          startY: currentY,
          head: [
            [
              "Drug",
              "Timing",
              "Frequency",
              "Dosage",
              "Additional Instructions",
              "Route",
              "Duration",
              //  "Instruction",
              "Reason",
              "Note",
            ],
          ],
          body: selectedSectionsData["Medications"].map((item) => [
            item.term || "-",
            [item?.time, item?.when].filter(Boolean).join(" ") || "-",
            item?.frequency || "-",
            item?.dosage || "-",
            Array.isArray(item?.additionalInstructions)
              ? item?.additionalInstructions.map((option) => option.term).join(", ")
              : item?.additionalInstructions || "-",
            item.selectedAdministration?.label || "-",
            item.duration ? item.duration : "-",
            // item.instructions || "-",
            item.reasons.join(", ") || "-",
            item.medicineNote || "-",
          ]),
          theme: "striped",
          styles: { fontSize: 8 },
          headStyles: {
            fillColor: hexToRgbArray("#EEEEEE"),
            textColor: hexToRgbArray("#000000"),
          },
          margin: {
            top: 250 + PRINT_OFFSET_TOP,
            left: EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
            right: EFFECTIVE_RIGHT_MARGIN,
            bottom: EFFECTIVE_BOTTOM_MARGIN,
          },
          didDrawPage: (data) => renderHeaderFooter(data, doc),
        });

        currentY = doc.lastAutoTable.finalY + 20;
      }

      currentY = renderProceduresToPdf(
        doc,
        currentY,
        themeColour,
        selectedSectionsData["Procedures"] || []
      );

      currentY = renderMedicalAdviceToPdf(
        doc,
        currentY,
        themeColour,
        selectedSectionsData["MedicalAdvice"] || {}
      );

      currentY = renderMedicalAdviceToPdf(
        doc,
        currentY,
        themeColour,
        { advice: selectedSectionsData["Notes"]?.note },
        "Notes"
      );

      // 6. Render Follow-up & Referral
      currentY = renderAppointmentToPdf(
        doc,
        currentY,
        themeColour,
        selectedSectionsData["FollowUp"],
        selectedSectionsData["Referral"],
        selectedSectionsData["IPDAdmissionReferral"] || []
      );

      // Rendering doctor signature at the end (only on last page)
      if (templateConfig.showDoctorSignature) {
        currentY = ensureSpaceForSection(doc, currentY, 110);
        currentY = renderDoctorSignatureBlock(doc, currentY);
      }

      // Ensure footer is added on every page (even non-autoTable ones)
      const totalPages = doc.internal.getNumberOfPages();

      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        renderHeaderFooter({}, doc); // add header
        if (SHOW_FOOTER) {
          renderFooter(doc, i); // add footer with correct page number
        }
      }

      // Post-process: optionally compose a new PDF with the template as background
      let finalBytes;
      if (shouldUseTemplate) {
        try {
          const srcBytes = doc.output("arraybuffer");
          const templateBytes = Uint8Array.from(atob(templateBase64), (c) =>
            c.charCodeAt(0)
          );

          const newDoc = await PDFDocument.create();
          const srcDoc = await PDFDocument.load(srcBytes);
          const pageCount = srcDoc.getPageCount();

          for (let i = 0; i < pageCount; i++) {
            const [embeddedSrcPage] = await newDoc.embedPdf(srcBytes, [i]);
            const [embeddedTemplatePage] = await newDoc.embedPdf(
              templateBytes,
              [0]
            );

            const pageWidth = embeddedSrcPage.width;
            const pageHeight = embeddedSrcPage.height;

            const page = newDoc.addPage([pageWidth, pageHeight]);
            // Draw template first (background, full-bleed)
            page.drawPage(embeddedTemplatePage, {
              x: 0,
              y: 0,
              width: pageWidth,
              height: pageHeight,
            });
            // Draw generated jsPDF page content on top
            page.drawPage(embeddedSrcPage, {
              x: 0,
              y: 0,
              width: pageWidth,
              height: pageHeight,
            });
          }

          finalBytes = await newDoc.save();
        } catch (e) {
          // If overlay fails, fall back to plain jsPDF output
          console.error("PDF template overlay failed:", e);
          finalBytes = doc.output("arraybuffer");
        }
      } else {
        // No background template; use the jsPDF output directly
        finalBytes = doc.output("arraybuffer");
      }

      // Open in a new window and trigger print
      const blob = new Blob([finalBytes], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(blob);
      const printWindow = window.open(blobUrl, "_blank");

      if (printWindow) {
        printWindow.onload = () => {
          printWindow.focus();
          printWindow.print();
        };
      } else {
        alert("Please allow pop-ups for this site to print the document.");
      }
    };

    const hasObservationValue = (value) => {
      if (value === 0 || value === "0") return true;
      if (value === undefined || value === null) return false;
      return String(value).trim().length > 0;
    };

    const getPanelsWithObservationValues = (panels = []) =>
      Array.isArray(panels)
        ? panels
          .map((panel) => ({
            ...panel,
            tests: (panel?.tests || []).filter((test) =>
              hasObservationValue(test?.value)
            ),
          }))
          .filter((panel) => panel.tests.length > 0)
        : [];

    const renderInvestigationToPdf = (
      doc,
      startY,
      themeColour,
      investigationData
    ) => {
      if (!Array.isArray(investigationData) || investigationData.length === 0) {
        return startY;
      }

      startY = ensureSpaceForSection(doc, startY);
      doc.setFontSize(14);
      doc.setTextColor("#000");
      startY += 10;
      doc.text(
        "Tests Advised",
        EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
        startY
      );
      startY += 15;

      const content = investigationData.map((data, index) => {
        const line1 = `${index + 1}. ${data.term || ""}${data.body_part ? `, ${data.body_part}` : ""
          }${data.laterality ? `, ${data.laterality}` : ""}${data.position ? `, ${data.position}` : ""
          }`;

        const line2 = data.clinical_note
          ? `Clinical Note - ${data.clinical_note}`
          : "";

        return [`${line1}${line2 ? `\n${line2}` : ""}`];
      });

      autoTable(doc, {
        startY,
        head: [["Tests Advised"]],
        body: content,
        theme: "grid",
        styles: { fontSize: 9 },
        // headStyles: { fillColor: hexToRgbArray("#EEEEEE") },
        headStyles: {
          fillColor: hexToRgbArray("#EEEEEE"),
          textColor: hexToRgbArray("#000000"),
        },
        margin: {
          top: 250 + PRINT_OFFSET_TOP,
          left: EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
          right: EFFECTIVE_RIGHT_MARGIN,
          bottom: EFFECTIVE_BOTTOM_MARGIN,
        },
        didDrawPage: (data) => renderHeaderFooter(data, doc),
        pageBreak: "auto",
      });

      return doc.lastAutoTable.finalY + 20;
    };
    const renderInitialObservationToPdf = (doc, startY, themeColour, data) => {
      const panelsWithValues = getPanelsWithObservationValues(data);
      if (panelsWithValues.length === 0) return startY;

      startY = ensureSpaceForSection(doc, startY);
      startY += 10; // small controlled space before header
      doc.setFontSize(14);
      doc.setTextColor("#000");
      doc.text(
        "Initial Observation",
        EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
        startY
      );
      startY += 15;

      const tableBody = [];

      panelsWithValues.forEach((panel) => {
        panel.tests.forEach((test, index) => {
          tableBody.push([
            index === 0 ? panel.panel_name : "",
            test.test || "-",
            test.value || "-",
            test.unit || "-",
          ]);
        });
      });

      if (tableBody.length === 0) return startY;

      autoTable(doc, {
        startY,
        head: [["Panel", "Test Name", "Value", "Unit"]],
        body: tableBody,
        theme: "grid",
        styles: { fontSize: 9 },
        headStyles: {
          fillColor: hexToRgbArray("#EEEEEE"),
          textColor: hexToRgbArray("#000000"),
        },
        margin: {
          top: 250 + PRINT_OFFSET_TOP,
          left: EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
          right: EFFECTIVE_RIGHT_MARGIN,
          bottom: EFFECTIVE_BOTTOM_MARGIN,
        },
        didDrawPage: (data) => renderHeaderFooter(data, doc),
        pageBreak: "auto",
      });

      return doc.lastAutoTable.finalY + 10;
    };

    const renderHeaderFooter = (data, doc) => {
      const pageNumber = doc.internal.getNumberOfPages();
      const midX = pageWidth / 2 + 10 + PRINT_OFFSET_LEFT; // shift right-column content

      const currentY = EFFECTIVE_TOP_MARGIN + PRINT_OFFSET_TOP; // use margin if needed
      // Draw optional overlay watermark (Rx text or image) on top of content
      try {
        const overlayCenterX = pageWidth / 2 + PRINT_OFFSET_LEFT / 2;
        const overlayCenterY = pageHeight / 2;
        // If jsPDF supports graphics state, apply low opacity
        if (SHOW_RX_OVERLAY) {
          if (doc.setGState && doc.GState) {
            doc.setGState(new doc.GState({ opacity: RX_OVERLAY_OPACITY }));
          }
          if (overlayImageBase64) {
            // Draw provided base64 image overlay covering the printable area
            try {
              doc.addImage(
                `data:image/png;base64,${overlayImageBase64}`,
                "PNG",
                PRINT_OFFSET_LEFT,
                PRINT_OFFSET_TOP,
                pageWidth - 2 * (EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT),
                pageHeight - (PRINT_OFFSET_TOP + 120)
              );
            } catch (imgErr) {
              // Fallback to Rx text if image draw fails
              doc.setFontSize(140);
              doc.setTextColor(180, 180, 180);
              doc.text("Rx", overlayCenterX, overlayCenterY, {
                angle: -30,
                align: "center",
              });
            }
          } else {
            // Default Rx watermark text
            doc.setFontSize(140);
            doc.setTextColor(180, 180, 180);
            doc.text("Rx", overlayCenterX, overlayCenterY, {
              angle: -30,
              align: "center",
            });
          }
          // Reset opacity if supported
          if (doc.setGState && doc.GState) {
            doc.setGState(new doc.GState({ opacity: 1 }));
          }
        }
      } catch (e) {
        // no-op if watermark drawing fails
      }
      const brandingItem = brandingData?.find(
        (item) => item.name === "branding_statements"
      );
      console.log("brandingItem", brandingItem);
      doc.setFontSize(14);
      doc.setTextColor("#000");

      const clinicAddress = [
        sessionStorage.getItem("facilityAddressLine1"),
        sessionStorage.getItem("facilityAddressLine2"),
        sessionStorage.getItem("facilityAddressLine3"),
      ]
        .filter(Boolean)
        .join(", ");

      doc.setFontSize(10);

      const doctorY = currentY + 40;
      doc.setFontSize(10);
      doc.setTextColor("#000");

      // Predeclare values used later for spacing
      const rightColumnLineHeight = 12;
      const clinicAddressY = doctorY + 45;
      let wrappedClinicAddress = [];
      let leftY = doctorY;
      //#"OP Consultation Report print button dropdown"
      if (SHOW_TOP_HEADER) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.text(
          "OP Consultation Report",
          EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
          currentY
        );
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);

        if (collateralImage) {
          const collateralImageDataUrl = collateralImage.startsWith("data:")
            ? collateralImage
            : `data:image/jpeg;base64,${collateralImage}`;
          try {
            const imageProps = doc.getImageProperties(collateralImageDataUrl);
            const { width: scaledWidth, height: scaledHeight } =
              getScaledDimensions(imageProps.width, imageProps.height, 110, 50);
            doc.addImage(
              collateralImageDataUrl,
              imageProps.fileType || "JPEG",
              pageWidth - scaledWidth - 60,
              currentY - 20,
              scaledWidth,
              scaledHeight
            );
          } catch (imgErr) {
            console.error("Failed to render collateral logo", imgErr);
          }
        }

        // Doctor Name — bold, larger font
        doc.setFont("helvetica", "bold");
        doc.setFontSize(14);
        doc.text(
          displayDoctorName(doctorData),
          EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
          doctorY
        );
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        leftY = doctorY + 16;

        // Degree / Qualifications
        const qualificationsText = `${doctorData?.qualifications || ""}`;
        if (qualificationsText.trim()) {
          const wrappedQualifications = doc.splitTextToSize(
            qualificationsText,
            pageWidth / 2 - 40
          );
          doc.text(
            wrappedQualifications,
            EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
            leftY
          );
          leftY += wrappedQualifications.length * 12;
        }

        // Registration No
        doc.text(
          `Reg No: ${doctorData?.registration_no ?? "NA"}`,
          EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
          leftY
        );
        leftY += 12;

        // Contact No
        if (doctorData?.phone_no) {
          doc.text(
            `Contact: ${doctorData.phone_no}`,
            EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
            leftY
          );
          leftY += 12;
        }

        // Email
        if (doctorData?.email) {
          doc.text(
            `Email: ${doctorData.email}`,
            EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
            leftY
          );
          leftY += 12;
        }

        if (brandingItem?.content?.collateralBrandStatement) {
          doc.text(
            brandingItem.content.collateralBrandStatement,
            pageWidth - 40,
            doctorY,
            {
              align: "right",
            }
          );
        }
        const formattedDate = formatDateIST(dateForAppointment?.toISOString());

        doc.text(`Date: ${formattedDate.split(" ")[0]}`, midX, doctorY + 15);
        doc.text(
          isTodayOrFuture(dateForAppointment) ? `Time: ${formatDateIST(new Date().toISOString()).split(" ").slice(1).join(" ")}` : `Time: ${startTime}`,
          midX,
          doctorY + 30
        );
        // Wrap clinic address to fit remaining right-column width after shifting right
        const rightColumnWidth = Math.max(80, pageWidth - midX - 40);
        const clinicAddressLine = `Clinic Address: ${clinicAddress}`;
        wrappedClinicAddress = doc.splitTextToSize(
          clinicAddressLine,
          rightColumnWidth
        );
        doc.text(wrappedClinicAddress, midX, clinicAddressY);
      }

      // Ensure patient section starts below wrapped clinic address (if shown)
      const patientY = Math.max(
        leftY + 5,
        clinicAddressY + wrappedClinicAddress.length * rightColumnLineHeight + 5
      );
      // LINE 1 — Patient Summary
      const patientLine = [
        patientData?.patient_id ? `ID: ${patientData.patient_id}` : null,
        `Patient: ${displayPatientName(patientData?.name)}`,
      ]
        .filter(Boolean)
        .join(" | ");
      doc.setFontSize(10);
      const availableWidth =
        pageWidth -
        (EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT) -
        EFFECTIVE_RIGHT_MARGIN;
      const lineHeight = 12;
      const lineSpacing = 5; // Space between lines

      // Calculate wrapped lines for first patient line
      const wrappedPatientLine = doc.splitTextToSize(
        patientLine,
        availableWidth
      );
      const patientLineHeight = wrappedPatientLine.length * lineHeight;

      // Render first line
      doc.text(
        wrappedPatientLine,
        EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
        patientY
      );

      // Calculate Y position for second line (first line height + spacing)
      let patientSectionY = patientY + patientLineHeight + lineSpacing;

      // LINE 2 — Appt | Ph | Age | G | DT
      const appointmentDateTime = (() => {
        if (!dateForAppointment) return null;
        const formattedDateTime = formatDateIST(dateForAppointment.toISOString());
        const datePart = formattedDateTime?.split(" ")?.[0];
        const timePart = isTodayOrFuture(dateForAppointment)
          ? formatDateIST(new Date().toISOString()).split(" ").slice(1).join(" ")
          : startTime;
        if (!datePart && !timePart) return null;
        return `DT: ${[datePart, timePart].filter(Boolean).join(" ")}`;
      })();
      const patientContactLine = [
        `Appt: ${formatAppointmentTypeForPdf(
          patientData?.appointment_type,
          patientData?.appointment_mode
        )}`,
        patientData?.phone_no ? `Ph: ${patientData.phone_no}` : null,
        patientData?.age ? `Age: ${patientData.age}y` : null,
        patientData?.gender ? `G: ${patientData.gender}` : null,
        appointmentDateTime,
      ]
        .filter(Boolean)
        .join(" | ");
      if (patientContactLine) {
        // Calculate wrapped lines for contact line
        const wrappedContactLine = doc.splitTextToSize(
          patientContactLine,
          availableWidth
        );
        const contactLineHeight = wrappedContactLine.length * lineHeight;

        // Render second line
        doc.text(
          wrappedContactLine,
          EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
          patientSectionY
        );

        // Calculate Y position for third line
        patientSectionY += contactLineHeight + lineSpacing;
      }

      // ABHA details on a single line when either field is present
      const abhaParts = [];
      if (patientData?.abha_id) {
        abhaParts.push(`ABHA Address: ${patientData.abha_id}`);
      }
      if (patientData?.abha_no) {
        abhaParts.push(`ABHA Number: ${patientData.abha_no}`);
      }
      if (abhaParts.length > 0) {
        const wrappedAbhaLine = doc.splitTextToSize(
          abhaParts.join(" | "),
          availableWidth
        );
        doc.text(
          wrappedAbhaLine,
          EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
          patientSectionY
        );
        patientSectionY += wrappedAbhaLine.length * lineHeight + lineSpacing;
      }

      // SMALL SEPARATOR LINE (slightly tighter gap after ABHA/details)
      const separatorY = patientSectionY + 2;
      doc.line(
        EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
        separatorY,
        pageWidth - EFFECTIVE_RIGHT_MARGIN,
        separatorY
      );
    };

    const renderFooter = (doc, pageNumber) => {
      if (!SHOW_FOOTER) return;
      const brandingItem = brandingData?.find(
        (item) => item.name === "branding_statements"
      );
      const themeColour = "#EEEEEE";
      const margin = 5;

      const centerX = pageWidth / 2;

      const departmentText = departments.length
        ? departments.map((d) => d.department).join(" | ")
        : "N/A";

      const poweredText =
        "Enhanced Patient Care Powered by Hlthtek Digital Healthcare Platform (Visit: https://www.hlthtek.com)";
      const logoImg = NavLogoFooter;

      const deptText = `Departments: ${departmentText}`;
      const wrappedDeptText = doc.splitTextToSize(
        deptText,
        pageWidth - 2 * margin
      );
      const deptLineCount = wrappedDeptText.length;
      const lineHeight = 12;
      const baseFooterHeight = 60;

      const dynamicFooterHeight =
        baseFooterHeight + (deptLineCount - 1) * lineHeight;

      const pageBottomY = pageHeight - margin;
      const footerTopY = pageBottomY - dynamicFooterHeight;

      // Footer Background
      doc.setFillColor(...hexToRgbArray(themeColour));
      const footerWidth =
        pageWidth - EFFECTIVE_LEFT_MARGIN - EFFECTIVE_RIGHT_MARGIN;
      const footerLeftX = EFFECTIVE_LEFT_MARGIN;

      doc.rect(footerLeftX, footerTopY, footerWidth, dynamicFooterHeight, "F");

      // White Text Inside Footer
      doc.setFontSize(9);
      doc.setTextColor("#fff");
      doc.text(wrappedDeptText, centerX, footerTopY + 15, {
        align: "center",
      });

      const poweredY = footerTopY + 15 + deptLineCount * lineHeight;
      doc.setFontSize(9);
      doc.text(poweredText, centerX, poweredY, {
        align: "center",
      });

      // Logo below powered text
      if (logoImg) {
        doc.addImage(logoImg, "PNG", centerX - 15, poweredY + 8, 30, 15);
      }
    };

    const renderDoctorSignatureBlock = (doc, startY) => {
      const signature = doctorData?.doctor_signature;
      const imgType = signature ? getImageType(signature) : null;

      const blockWidth = 180; // Width of right-side signature block
      const blockX = pageWidth - blockWidth - EFFECTIVE_RIGHT_MARGIN; // right margin from config
      let y = startY;

      doc.setFontSize(11);
      doc.setTextColor("#000");

      // Signature Image (if available)
      if (signature) {
        doc.addImage(
          `data:image/${imgType};base64,${signature}`,
          imgType.toUpperCase(),
          blockX,
          y,
          140,
          60
        );
        y += 70;
      }

      // Doctor Name
      doc.setFont("helvetica", "bold");
      doc.text(displayDoctorName(doctorData), blockX, y);
      y += 14;

      doc.setFont("helvetica", "normal");

      // Qualifications (wrapped)
      if (doctorData?.qualifications) {
        const wrappedQual = doc.splitTextToSize(
          doctorData.qualifications,
          blockWidth
        );
        doc.text(wrappedQual, blockX, y);
        y += wrappedQual.length * 12;
      }

      // Department + Specialization
      const deptLine = `${doctorData?.department || ""}${doctorData?.specialization ? " - " + doctorData.specialization : ""
        }`;
      if (deptLine.trim().length > 0) {
        const wrappedDept = doc.splitTextToSize(deptLine, blockWidth);
        doc.text(wrappedDept, blockX, y);
        y += wrappedDept.length * 12;
      }

      // Registration Number
      doc.text(
        `Reg No: ${doctorData?.registration_no ?? "NA"}`,
        blockX,
        y + 10
      );

      return y + 30; // final Y pointer
    };

    const getImageType = (base64String) => {
      if (!base64String) return null;

      if (base64String.startsWith("/9j/")) {
        return "jpeg";
      } else if (base64String.startsWith("iVBORw0KGgo")) {
        return "png";
      } else {
        return "jpeg"; // Default fallback
      }
    };

    const getScaledDimensions = (width, height, maxWidth, maxHeight) => {
      if (!width || !height) {
        return { width: maxWidth, height: maxHeight };
      }
      const widthRatio = maxWidth / width;
      const heightRatio = maxHeight / height;
      const scale = Math.min(widthRatio, heightRatio, 1);
      return {
        width: width * scale,
        height: height * scale,
      };
    };

    // Helpers
    const hexToRgbArray = (hex) => {
      const bigint = parseInt(hex.replace("#", ""), 16);
      return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255];
    };

    const getHeaders = (sectionName) => {
      switch (sectionName) {
        case "History of Past Illness":
        case "History of Present Illness":
          return [
            "Condition",
            "Clinical Notes",
            // "Duration",
            "Clinical Status",
            "Condition Onset",
            "Condition Duration",
          ];
        case "Family History":
          return [
            "Relation",
            "Condition",
            "On Set Age",
            "Deceased",
            "Notes",
            "Condition Onset",
            "Condition Duration",
          ];
        case "Drug History":
          return ["Drug Name", "Drug Onset", "Drug Duration", "Notes"];
        case "Allergies":
          return [
            "Type",
            "Description",
            "Note",
            "Status",
            "Allergy Onset",
            "Allergy Duration",
          ];
        case "Addiction History":
          return [
            "Type",
            "Description",
            "Addiction Onset",
            "Addiction Duration",
          ];
        case "Surgeries":
          return ["Procedure", "Notes", "Follow Up", "Time Since Surgery"];
        case "Menstrual History":
          return ["Type", "Description", "Time Since Observation"];
        case "Adult Vaccination":
        case "Adult Vaccinations":
          return ["Vaccine Name", "Dose", "Date of Administration"];
        default:
          return [];
      }
    };

    const formatRowData = (sectionName, item) => {
      switch (sectionName) {
        case "History of Past Illness":
        case "History of Present Illness":
          return [
            item.condition,
            item.clinical_note || "-",
            // `${item.duration} ${item.chiefComplaintDurationType || ""}`,
            item.clinical_status_description || "-",
            formatHistoryOnsetForPdf(item, "onset_datetime", "onset_duration", "onset_duration_type"),
            formatHistoryDurationForPdf(
              item,
              "onset_datetime",
              "abatement_datetime",
              "abatement_duration",
              "abatement_duration_type"
            ),
          ];
        case "Family History":
          return [
            item.relation,
            item.condition,
            item.onSetAge,
            item.deceased ? "Yes" : "No",
            item.clinical_note || "-",
            formatHistoryOnsetForPdf(item, "onset_datetime", "onset_duration", "onset_duration_type"),
            formatHistoryDurationForPdf(
              item,
              "onset_datetime",
              "abatement_datetime",
              "abatement_duration",
              "abatement_duration_type"
            ),
          ];
        case "Drug History":
          return [
            item.drug_name,
            formatHistoryOnsetForPdf(item, "onset_datetime", "condition_onset", "condition_onset_type"),
            formatHistoryDurationForPdf(
              item,
              "onset_datetime",
              "abatement_datetime",
              "duration",
              "durationType"
            ),
            item.clinical_note || "-",
          ];
        case "Allergies":
          return [
            item.allergy_type,
            item.allergy_description,
            item.note || "-",
            item.clinical_status_description || "-",
            formatHistoryOnsetForPdf(item, "onset_datetime", "condition_onset", "condition_onset_type"),
            formatHistoryDurationForPdf(
              item,
              "onset_datetime",
              "abatement_datetime",
              "condition_duration",
              "condition_duration_type"
            ),
          ];
        case "Addiction History":
          return [
            item.addiction_type,
            item.addiction_description,
            item?.onset_datetime
              ? formatOnsetDurationForPdf(
                getHumanReadableDiffInPastDateWithAgo(item?.onset_datetime, item)
              )
              : "-",
            item?.abatement_datetime
              ? formatOnsetDurationForPdf(
                getHumanReadableDurationInDate(
                  item?.onset_datetime,
                  item?.abatement_datetime,
                  item
                )
              )
              : "-",
          ];
        case "Surgeries":
          return [
            item.procedure,
            item.clinical_note || "-",
            item.follow_up_description || "-",
            item?.recorded_date
              ? formatOnsetDurationForPdf(
                getHumanReadableDiffInPastDateWithAgo(item?.recorded_date, item)
              )
              : "-",
          ];
        case "Menstrual History":
          return [
            item.menstrualType || "-",
            item.description,
            item?.duration
              ? formatOnsetDurationForPdf(item.duration)
              : item?.durationType
                ? formatOnsetDurationForPdf(
                  `${item?.duration || ""} ${item.durationType}`.trim()
                )
                : item?.date
                  ? formatOnsetDurationForPdf(
                    getHumanReadableDiffInPastDateWithAgo(item?.date, item)
                  )
                  : "-",
          ];
        case "Adult Vaccination":
        case "Adult Vaccinations":
          return [
            item?.vaccine_name || item?.vaccine || item?.name || "-",
            item?.dose || "-",
            item?.date_of_administration || item?.date || "-",
          ];
        default:
          return [];
      }
    };
    // Utility Functions for rendering additional sections in PDF

    const renderExaminationsToPdf = (
      doc,
      startY,
      themeColour,
      vitals,
      bloodGroup,
      otherExaminations,
      dentalData
    ) => {
      const otherExaminationsText =
        typeof otherExaminations === "string" ? otherExaminations.trim() : "";
      if (
        !hasVitalValue &&
        !hasDentalData &&
        !bloodGroup &&
        !otherExaminationsText
      ) {
        return startY; // nothing to render
      }

      let currentY = startY;
      currentY = ensureSpaceForSection(doc, currentY);
      currentY += 10; //Added a little extra spacing

      doc.setFontSize(14);
      doc.setTextColor("#000");
      doc.text(
        "Examination/Vitals",
        EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
        currentY
      );
      currentY += 15;

      // 1) Unified Vitals/Wellness + Blood Group in one horizontal row
      {
        const head = [[]];
        const row = [[]];

        if (vitals?.length > 0 && hasVitalValue) {
          const vitalsWithValues = vitals.filter((v) => v?.value !== "");
          vitalsWithValues.forEach((v) => {
            head[0].push(v.name);
            row[0].push(`${v.value} ${v.sign || ""}`.trim());
          });

          // Append BMI if both height and weight are present
          const height = vitals.find((i) => i.name === "Height")?.value;
          const weight = vitals.find((i) => i.name === "Weight")?.value;
          if (height && weight) {
            const bmi = (
              parseFloat(weight) / Math.pow(parseFloat(height) / 100, 2)
            ).toFixed(2);
            head[0].push("BMI");
            row[0].push(`${bmi} kg/m²`);
          }
        }

        if (bloodGroup) {
          head[0].push("Blood Group");
          row[0].push(bloodGroup);
        }

        if (head[0].length > 0) {
          autoTable(doc, {
            startY: currentY,
            head,
            body: row,
            theme: "grid",
            styles: { fontSize: 9 },
            headStyles: {
              fillColor: hexToRgbArray("#EEEEEE"),
              textColor: hexToRgbArray("#000000"),
            },
            margin: {
              top: 250 + PRINT_OFFSET_TOP,
              left: EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
              right: EFFECTIVE_RIGHT_MARGIN,
              bottom: EFFECTIVE_BOTTOM_MARGIN,
            },
            didDrawPage: (data) => renderHeaderFooter(data, doc),
            pageBreak: "auto",
          });
          currentY = doc.lastAutoTable.finalY + 10;
        }
      }

      // 2) Other Examinations free text
      if (otherExaminationsText) {
        autoTable(doc, {
          startY: currentY,
          head: [["Other Examinations"]],
          body: [[otherExaminationsText]],
          theme: "grid",
          styles: { fontSize: 9 },
          headStyles: {
            fillColor: hexToRgbArray("#EEEEEE"),
            textColor: hexToRgbArray("#000000"),
          },
          margin: {
            top: 250 + PRINT_OFFSET_TOP,
            left: EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
            right: EFFECTIVE_RIGHT_MARGIN,
            bottom: EFFECTIVE_BOTTOM_MARGIN,
          },
          didDrawPage: (data) => renderHeaderFooter(data, doc),
          pageBreak: "auto",
        });
        currentY = doc.lastAutoTable.finalY + 10;
      }

      // 3) Dental Examination aggregated into a single row (Teeth + Note)
      if (dentalData?.length > 0 && hasDentalData) {
        const visibleDental = dentalData.filter((d) => d?.value !== "");
        if (visibleDental.length > 0) {
          const teethNumbers = visibleDental
            .map((item) =>
              item.id
                .split(",")
                .map((id) => id.replace("_", ""))
                .join(", ")
            )
            .join("; ");
          const notes = visibleDental
            .map((item) => item?.clinical_note)
            .filter(Boolean)
            .join("; ");

          autoTable(doc, {
            startY: currentY,
            head: [["Teeth FDI No.", "Note"]],
            body: [[teethNumbers || "-", notes || "-"]],
            theme: "grid",
            styles: { fontSize: 9 },
            headStyles: {
              fillColor: hexToRgbArray("#EEEEEE"),
              textColor: hexToRgbArray("#000000"),
            },
            margin: {
              top: 250 + PRINT_OFFSET_TOP,
              left: EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
              right: EFFECTIVE_RIGHT_MARGIN,
              bottom: EFFECTIVE_BOTTOM_MARGIN,
            },
            didDrawPage: (data) => renderHeaderFooter(data, doc),
            pageBreak: "auto",
          });
          currentY = doc.lastAutoTable.finalY + 20;
        }
      }

      return currentY;
    };

    const renderMedicalAdviceToPdf = (
      doc,
      startY,
      themeColour,
      selectedSectionsData,
      title = "Medical Advice"
    ) => {
      if (
        !selectedSectionsData ||
        !selectedSectionsData.advice ||
        selectedSectionsData.advice.trim().length === 0
      ) {
        console.log("return-------------", selectedSectionsData);
        return startY;
      }
      console.log("i am in------------------------------------");
      startY = ensureSpaceForSection(doc, startY);
      startY += 10; //  adds a clean gap before "Medical Advice" header
      doc.setFontSize(14);
      doc.setTextColor("#000");
      doc.text(
        title,
        EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
        startY
      );
      startY += 15;

      autoTable(doc, {
        startY,
        head: [[title]],
        body: [[selectedSectionsData.advice]],
        theme: "striped",
        styles: { fontSize: 8 },
        headStyles: {
          fillColor: hexToRgbArray("#EEEEEE"),
          textColor: hexToRgbArray("#000000"),
        },
        margin: {
          top: 250 + PRINT_OFFSET_TOP,
          left: EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
          right: EFFECTIVE_RIGHT_MARGIN,
          bottom: EFFECTIVE_BOTTOM_MARGIN,
        },
        didDrawPage: (data) => renderHeaderFooter(data, doc),
        pageBreak: "auto",
      });

      return doc.lastAutoTable.finalY + 10;
    };

    const renderAppointmentToPdf = (
      doc,
      startY,
      themeColour,
      followUp,
      referral,
      ipdAdmissionReferral
    ) => {
      // if (
      //   !Array.isArray(followUp) ||
      //   followUp.length === 0 ||
      //   !Array.isArray(referral) ||
      //   referral.length === 0
      // ) {
      //   return startY;
      // }
      if (
        (!Array.isArray(followUp) || followUp.length === 0) &&
        (!Array.isArray(referral) || referral.length === 0) &&
        (!Array.isArray(ipdAdmissionReferral) ||
          ipdAdmissionReferral.length === 0)
      ) {
        return startY;
      }

      const followUpData = followUp[0];
      const referralData = referral[0];
      const ipdData =
        Array.isArray(ipdAdmissionReferral) && ipdAdmissionReferral.length > 0
          ? ipdAdmissionReferral[0]
          : null;

      // doc.setFontSize(14);
      // doc.setTextColor("#000");
      // doc.text("Follow-up & Referral", margin, startY);
      // startY += 15;

      const content = [];
      if (followUpData?.service_type) {
        startY = ensureSpaceForSection(doc, startY);
        startY += 10;
        doc.setFontSize(14);
        doc.setTextColor("#000");
        doc.text(
          "Follow-up & Referral",
          EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
          startY
        );
        startY += 15;
        if (followUpData.service_type)
          content.push([followUpData.service_type]);
        if (followUpData.duration)
          content.push([`${formatOnsetDurationForPdf(followUpData.duration)}`]);
      }

      if (referralData?.service_description) {
        content.push([
          `Referral Details - ${referralData.service_description}`,
        ]);
        if (referralData.service_category)
          content.push([`Department - ${referralData.service_category}`]);
        if (referralData.doctor_details?.name)
          content.push([`Doctor Name - ${referralData.doctor_details.name}`]);
      }

      if (ipdData?.admission_details) {
        if (!followUpData?.service_type && !referralData?.service_description) {
          startY = ensureSpaceForSection(doc, startY);
          startY += 10;
          doc.setFontSize(14);
          doc.setTextColor("#000");
          doc.text(
            "Follow-up & Referral",
            EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
            startY
          );
          startY += 15;
        }

        content.push([
          `IPD Admission Details - ${ipdData.admission_details}${ipdData.admission_type
            ? `, Type: ${ipdData.admission_type}`
            : ""
          }${ipdData.expected_date
            ? `, Expected Date: ${ipdData.expected_date?.split('T')[0]}`
            : ""
          }${ipdData.doctor_details?.name
            ? `, Doctor: ${ipdData.doctor_details.name}`
            : ""
          }${ipdData.service_category ? `, Department: ${ipdData.service_category}` : ""
          }`,
        ]);
      }

      if (content.length > 0) {
        autoTable(doc, {
          startY,
          head: [["Follow-up & Referral"]],
          body: content,
          theme: "grid",
          styles: { fontSize: 9 },
          headStyles: {
            fillColor: hexToRgbArray("#EEEEEE"),
            textColor: hexToRgbArray("#000000"),
          },
          margin: {
            top: 250 + PRINT_OFFSET_TOP,
            left: EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
            right: EFFECTIVE_RIGHT_MARGIN,
            bottom: EFFECTIVE_BOTTOM_MARGIN,
          },
          didDrawPage: (data) => renderHeaderFooter(data, doc),
          pageBreak: "auto",
        });
        return doc.lastAutoTable.finalY + 20;
      }

      return startY;
    };

    const renderOtherSectionsToPdf = (
      doc,
      startY,
      themeColour,
      sections = []
    ) => {
      if (!sections || sections.length === 0) return startY;

      startY = ensureSpaceForSection(doc, startY);
      doc.setFontSize(14);
      doc.setTextColor("#000");
      startY += 5;
      doc.text("Diagnosis", EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT, startY);
      startY += 15;

      const content = sections.map((item) => {
        const details = [
          item.duration
            ? `Duration - ${formatOnsetDurationForPdf(item.duration)}`
            : null,
          item.clinical_note ? `Clinical Note - ${item.clinical_note}` : null,
        ]
          .filter(Boolean)
          .join(" | ");

        return [
          `${item.term}`,
          details || "-",
        ];
      });

      autoTable(doc, {
        startY,
        head: [["Term", "Details"]],
        body: content,
        theme: "striped",
        styles: { fontSize: 9 },
        headStyles: {
          fillColor: hexToRgbArray("#EEEEEE"),
          textColor: hexToRgbArray("#000000"),
        },
        margin: {
          top: 250 + PRINT_OFFSET_TOP,
          left: EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
          right: EFFECTIVE_RIGHT_MARGIN,
          bottom: EFFECTIVE_BOTTOM_MARGIN,
        },
        didDrawPage: (data) => renderHeaderFooter(data, doc),
        pageBreak: "auto",
      });

      return doc.lastAutoTable.finalY + 20;
    };

    const renderAdultVaccinationToPdf = (
      doc,
      startY,
      themeColour,
      vaccinations = []
    ) => {
      if (!Array.isArray(vaccinations) || vaccinations.length === 0) return startY;

      startY = ensureSpaceForSection(doc, startY);
      doc.setFontSize(14);
      doc.setTextColor("#000");
      // Keep a safe gap from the header separator line to prevent visual overlap.
      startY += 12;
      doc.text(
        "Adult Vaccination",
        EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
        startY
      );
      startY += 15;

      const content = vaccinations.map((item) => [
        item?.vaccine_name || item?.vaccine || item?.name || "-",
        item?.dose || "-",
        item?.date_of_administration || item?.date || "-",
      ]);

      autoTable(doc, {
        startY,
        head: [["Vaccine Name", "Dose", "Date of Administration"]],
        body: content,
        theme: "striped",
        styles: { fontSize: 9 },
        headStyles: {
          fillColor: hexToRgbArray("#EEEEEE"),
          textColor: hexToRgbArray("#000000"),
        },
        margin: {
          top: 250 + PRINT_OFFSET_TOP,
          left: EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
          right: EFFECTIVE_RIGHT_MARGIN,
          bottom: EFFECTIVE_BOTTOM_MARGIN,
        },
        didDrawPage: (data) => renderHeaderFooter(data, doc),
        pageBreak: "auto",
      });

      return doc.lastAutoTable.finalY + 20;
    };

    const renderChiefComplaintsToPdf = (
      doc,
      startY,
      themeColour,
      selectedSectionsData
    ) => {
      if (!selectedSectionsData || selectedSectionsData.length === 0)
        return startY;
      startY = ensureSpaceForSection(doc, startY);
      doc.setFontSize(14);
      doc.setTextColor("#000");
      doc.text(
        "Chief Complaints",
        EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
        startY
      );
      startY += 15;
      autoTable(doc, {
        startY,
        head: [["Condition", "Duration", "Status", "Note"]],
        body: selectedSectionsData.map((item) => [
          item.condition || "-",
          item.duration && item.duration.length !== 0
            ? formatOnsetDurationForPdf(item.duration)
            : "-",
          item.clinical_status_description &&
            item.clinical_status_description.length !== 0
            ? item.clinical_status_description
            : "-",
          item.clinical_note && item.clinical_note.length !== 0
            ? item.clinical_note
            : "-",
        ]),
        theme: "striped",
        styles: { fontSize: 8 },
        headStyles: {
          fillColor: hexToRgbArray("#EEEEEE"),
          textColor: hexToRgbArray("#000000"),
        },
        margin: {
          top: 250 + PRINT_OFFSET_TOP,
          left: EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
          right: EFFECTIVE_RIGHT_MARGIN,
          bottom: EFFECTIVE_BOTTOM_MARGIN,
        },
        didDrawPage: (data) => renderHeaderFooter(data, doc),

        pageBreak: "auto",
      });

      return doc.lastAutoTable.finalY + 10;
    };

    const renderProceduresToPdf = (doc, startY, themeColour, procedures) => {
      if (!procedures || procedures.length === 0) return startY;
      startY = ensureSpaceForSection(doc, startY);
      doc.setFontSize(14);
      doc.setTextColor("#000");
      doc.text("Procedures", EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT, startY);
      startY += 15;
      autoTable(doc, {
        startY,
        head: [["Procedure", "Clinical Note"]],
        body: procedures.map((proc) => [
          proc.term || "-",
          proc.clinical_note && proc.clinical_note.length !== 0
            ? proc.clinical_note
            : "-",
        ]),
        theme: "striped",
        styles: { fontSize: 8 },
        headStyles: {
          fillColor: hexToRgbArray("#EEEEEE"),
          textColor: hexToRgbArray("#000000"),
        },
        margin: {
          top: 250 + PRINT_OFFSET_TOP,
          left: EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
          right: EFFECTIVE_RIGHT_MARGIN,
          bottom: EFFECTIVE_BOTTOM_MARGIN,
        },
        didDrawPage: (data) => renderHeaderFooter(data, doc),

        pageBreak: "auto",
      });

      return doc.lastAutoTable.finalY + 10;
    };

    const checkTotalHistoryData = (medicalBackground) => {
      for (const category in medicalBackground) {
        const { prev, new: newData } = medicalBackground[category];
        console.log("Prev and New", prev, newData);
        if (prev.length > 0) return true;
        if (newData.length > 0) return true;
      }
      return false;
    };
    useImperativeHandle(ref, () => ({
      print: (withHeaders, type) => handleDownload(withHeaders, type),
    }));
    console.log("SectionsContent", selectedSectionsData);
    return (
      <>
        {/* <button onClick={handleDownload} className="listening-button">
          Download
        </button> */}
      </>
    );
  }
);

export default React.memo(PrintPrescription);
